import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../../api/client";

// Active (non-archived) searches for the authed scout, most recent first.
export const useScoutSearches = (options = {}) =>
  useQuery({
    queryKey: ["scout-searches"],
    queryFn: () =>
      apiFetch("/scout-searches", {
        query: { sort: "updated_at", order: "desc", pageSize: 100 },
      }),
    select: (data) => data.items || [],
    ...options,
  });

export const useCreateScoutSearch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => apiFetch("/scout-searches", { method: "POST", body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["scout-searches"] }),
  });
};

export const useUpdateScoutSearch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...fields }) =>
      apiFetch(`/scout-searches/${id}`, { method: "PATCH", body: fields }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["scout-searches"] }),
  });
};

export const useArchiveScoutSearch = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiFetch(`/scout-searches/${id}/archive`, { method: "POST" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["scout-searches"] }),
  });
};

// Ranked matches for a search (replaces supabase.rpc("match_athletes_for_search")).
// Rows come back as { athlete_id, full_name, position, sport, category, age,
// country, avatar_url, points, score, reasons[] }; `id` is aliased for the UI.
// Scoring every athlete is the heaviest read in the app, and AuthContext keeps
// it mounted on every scout screen — don't redo it on every refocus.
export const useSearchMatches = (searchId, options = {}) =>
  useQuery({
    queryKey: ["scout-searches", searchId, "matches"],
    queryFn: () => apiFetch(`/scout-searches/${searchId}/matches`),
    select: (data) => (data.items || []).map((row) => ({ ...row, id: row.athlete_id })),
    enabled: Boolean(searchId) && (options.enabled ?? true),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
